import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Loader2, UtensilsCrossed } from 'lucide-react';
import { TagInput } from "@/components/ui/tag-input";
import { ChipSelector } from "@/components/ui/chip-selector";
import { CollapsibleChipList } from "@/components/ui/CollapsibleChipList";
import { CuisineSelector } from "@/components/ui/cuisine-selector";
import { SimpleChipSelector } from "@/components/ui/simple-chip-selector";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { RestaurantSearchInput } from './submission/RestaurantSearchInput';
import { ReviewMatrix, ReviewRatings } from './submission/ReviewMatrix';
import { TagSelector } from './submission/TagSelector';
import { trackEvent } from '@/services/analyticsService';
import useLocalStorage from '@/hooks/useLocalStorage';
import { SubmissionMediaUploader, SubmissionMediaUploaderHandle } from './submission/SubmissionMediaUploader';

// Draft storage key
const DRAFT_KEY = 'submission-draft';

const CORE_CATEGORY_OPTIONS = [
  { id: 'starter',  label: 'Starters / Small Plates', emoji: '🥗' },
  { id: 'main',     label: 'Mains',                   emoji: '🍽️' },
  { id: 'side',     label: 'Sides',                   emoji: '🥙' },
  { id: 'dessert',  label: 'Desserts',                emoji: '🍰' },
  { id: 'drink',    label: 'Drinks',                  emoji: '🥤' },
];

const TIME_OF_DAY_OPTIONS = [
  { id: 'breakfast_brunch', label: 'Breakfast & Brunch', emoji: '🌅' },
  { id: 'lunch',            label: 'Lunch',              emoji: '☀️'  },
  { id: 'dinner',           label: 'Dinner',             emoji: '🌙' },
  { id: 'late_night',       label: 'Late Night',         emoji: '🦉' },
];

interface SubmissionFlowProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'create' | 'edit';
  itemType?: 'dish' | 'product';
  itemToEdit?: any;
  // Pre-fill data from Snap/Drool flow
  prefillData?: {
    imageUrl: string;
    restaurantName: string;
    googlePlaceId: string;
    restaurantAddress?: string;
    restaurantLat?: number;
    restaurantLng?: number;
    serviceType: 'dine_in' | 'takeaway';
    // Video context - when creating dish from Drool flow
    mediaType?: 'image' | 'video';
    skipPostCreation?: boolean;
  };
  // Callback when dish is created
  onDishCreated?: (dishId: string, dishName: string) => void;
}

interface EatOutFormData {
  dishName: string;
  restaurantName: string;
  chef: string;
  price: number | null;
  description: string;
  imageUrls: string[];
  videoUrls: string[];
  cuisine: string[];
  tags: string[];
  selectedClaims: string[];
  selectedAllergens: string[];
  // Google Places data
  googlePlaceId?: string;
  restaurantAddress?: string;
  restaurantLat?: number;
  restaurantLng?: number;
  countryCode?: string;
  // Review ratings
  ratingValue: string | null;
  ratingPortion: string | null;
  ratingTaste: string | null;
  // Review tags (for posts table)
  reviewTags: string[];
  // Structured categories
  coreCategory: string | null;
  timeOfDay: string[];
}

interface EatInFormData {
  itemName: string;
  restaurantName: string;
  price: number | null;
  deliveryPlatform: string;
  itemLink: string;
  description: string;
  imageUrls: string[];
  videoUrls: string[];
  cuisine: string[];
  tags: string[];
  selectedClaims: string[];
  selectedAllergens: string[];
  // Google Places data
  googlePlaceId?: string;
  restaurantAddress?: string;
  restaurantLat?: number;
  restaurantLng?: number;
  countryCode?: string;
  // Review ratings
  ratingValue: string | null;
  ratingPortion: string | null;
  ratingTaste: string | null;
  // Review tags (for posts table)
  reviewTags: string[];
  // Structured categories
  coreCategory: string | null;
  timeOfDay: string[];
}

// EatOutForm Component
interface EatOutFormProps {
  id?: string;
  initialData: EatOutFormData | null;
  dietaryClaims: Array<{id: string, name: string, icon?: string}>;
  allergens: Array<{id: string, name: string, icon?: string}>;
  isSubmitting: boolean;
  mode: 'create' | 'edit';
  onSubmit: (data: EatOutFormData) => Promise<void>;
}

// Video context banner component
const VideoContextBanner: React.FC = () => (
  <div className="flex items-start gap-3 p-3 bg-primary/10 border border-primary/30 rounded-lg mb-4">
    <span className="text-lg">📹</span>
    <div className="flex-1">
      <p className="text-sm font-medium text-foreground">Creating a dish for your video</p>
      <p className="text-xs text-muted-foreground">Please upload a cover photo for the dish card below.</p>
    </div>
  </div>
);

interface EatOutFormPropsExtended extends EatOutFormProps {
  isVideoContext?: boolean;
  restaurantPrefilled?: boolean;
  onCanSubmitChange: (canSubmit: boolean) => void;
  mediaUploaderRef: React.RefObject<SubmissionMediaUploaderHandle>;
}

const EatOutForm: React.FC<EatOutFormPropsExtended> = ({
  id,
  initialData,
  dietaryClaims,
  allergens,
  isSubmitting,
  mode,
  onSubmit,
  isVideoContext = false,
  restaurantPrefilled = false,
  onCanSubmitChange,
  mediaUploaderRef,
}) => {
  const eatOutForm = useForm<EatOutFormData>({
    defaultValues: initialData || {
      dishName: '',
      restaurantName: '',
      chef: '',
      price: null,
      description: '',
      imageUrls: [],
      videoUrls: [],
      cuisine: [],
      tags: [],
      selectedClaims: [],
      selectedAllergens: [],
      googlePlaceId: undefined,
      restaurantAddress: undefined,
      restaurantLat: undefined,
      restaurantLng: undefined,
      countryCode: undefined,
      ratingValue: null,
      ratingPortion: null,
      ratingTaste: null,
      reviewTags: [],
      coreCategory: null,
      timeOfDay: []
    }
  });

  useEffect(() => {
    if (initialData) {
      eatOutForm.reset(initialData);
    }
  }, [initialData, eatOutForm]);

  const watchedDishName = eatOutForm.watch('dishName');
  const watchedRestaurantNameOut = eatOutForm.watch('restaurantName');
  const watchedImageUrlsOut = eatOutForm.watch('imageUrls');

  useEffect(() => {
    onCanSubmitChange(
      watchedDishName.trim() !== '' &&
      watchedRestaurantNameOut.trim() !== '' &&
      watchedImageUrlsOut.length > 0
    );
  }, [watchedDishName, watchedRestaurantNameOut, watchedImageUrlsOut, onCanSubmitChange]);

  return (
    <div>
      <form id={id} onSubmit={eatOutForm.handleSubmit(onSubmit)} className="space-y-6">
        {/* Video Context Banner */}
        {isVideoContext && <VideoContextBanner />}
        
        {/* Hero Section - Photos & Videos */}
        <SubmissionMediaUploader
          ref={mediaUploaderRef}
          imageUrls={eatOutForm.watch('imageUrls')}
          videoUrls={eatOutForm.watch('videoUrls')}
          onImagesChange={(urls) => eatOutForm.setValue('imageUrls', urls)}
          onVideosChange={(urls) => eatOutForm.setValue('videoUrls', urls)}
          isVideoContext={isVideoContext}
        />

        {/* Identity Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <RestaurantSearchInput
              initialValue={eatOutForm.watch('restaurantName')}
              disabled={restaurantPrefilled}
              onSelect={(data) => {
                eatOutForm.setValue('restaurantName', data.name);
                eatOutForm.setValue('googlePlaceId', data.place_id);
                eatOutForm.setValue('restaurantAddress', data.address);
                eatOutForm.setValue('restaurantLat', data.lat);
                eatOutForm.setValue('restaurantLng', data.lng);
                eatOutForm.setValue('countryCode', data.country_code);
              }}
            />
          </div>
          <div>
            <Label className="text-foreground font-medium">Dish Name *</Label>
            <Input
              placeholder={eatOutForm.watch('googlePlaceId') ? 'e.g., Truffle Pasta' : 'Select a restaurant first'}
              {...eatOutForm.register('dishName')}
              disabled={!eatOutForm.watch('googlePlaceId')}
              className="mt-1 border-border bg-background text-foreground disabled:bg-muted/50 disabled:text-muted-foreground disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-foreground font-medium">Chef</Label>
            <Input
              placeholder="e.g., Chef Marco"
              {...eatOutForm.register('chef')}
              className="mt-1 border-border bg-background text-foreground"
            />
          </div>
          <div>
            <Label className="text-foreground font-medium">Price</Label>
            <Input
              placeholder="0.00"
              type="number"
              step="0.01"
              {...eatOutForm.register('price', { valueAsNumber: true })}
              className="mt-1 border-border bg-background text-foreground"
            />
          </div>
        </div>

        {/* Review Section */}
        <ReviewMatrix
          value={{
            ratingValue: eatOutForm.watch('ratingValue') as any,
            ratingPortion: eatOutForm.watch('ratingPortion') as any,
            ratingTaste: eatOutForm.watch('ratingTaste') as any
          }}
          onChange={(key, value) => eatOutForm.setValue(key as any, value)}
        />

        {/* Tag Selector */}
        <TagSelector
          selected={eatOutForm.watch('reviewTags')}
          onChange={(tags) => eatOutForm.setValue('reviewTags', tags)}
        />

        {/* Classification Section */}
        <div>
          <Label className="text-foreground font-medium mb-2 block">Cuisine Type</Label>
          <CuisineSelector
            value={eatOutForm.watch('cuisine')}
            onChange={(value) => eatOutForm.setValue('cuisine', value)}
          />
        </div>

        <div>
          <Label className="text-foreground font-medium mb-2 block">Dish Type</Label>
          <SimpleChipSelector
            options={CORE_CATEGORY_OPTIONS}
            selected={eatOutForm.watch('coreCategory') ? [eatOutForm.watch('coreCategory')!] : []}
            onChange={(vals) => eatOutForm.setValue('coreCategory', vals[0] ?? null)}
            mode="single"
            columns={3}
          />
        </div>

        <div>
          <Label className="text-foreground font-medium mb-2 block">Best For</Label>
          <SimpleChipSelector
            options={TIME_OF_DAY_OPTIONS}
            selected={eatOutForm.watch('timeOfDay')}
            onChange={(vals) => eatOutForm.setValue('timeOfDay', vals)}
            mode="multiple"
            columns={2}
          />
        </div>

        <div>
          <Label className="text-foreground font-medium mb-2 block">Dietary Claims</Label>
          <CollapsibleChipList
            options={dietaryClaims.map(claim => ({
              id: claim.id,
              name: claim.name,
              iconKey: claim.icon || null
            }))}
            selected={eatOutForm.watch('selectedClaims')}
            onChange={(selected) => eatOutForm.setValue('selectedClaims', selected)}
            maxVisible={6}
          >
            {(visibleOptions) => (
              <ChipSelector
                options={visibleOptions}
                selected={eatOutForm.watch('selectedClaims')}
                onChange={(selected) => eatOutForm.setValue('selectedClaims', selected)}
                mode="multiple"
                colorScheme="green"
                type="dietary"
              />
            )}
          </CollapsibleChipList>
        </div>

        <div>
          <Label className="text-foreground font-medium mb-2 block">Contains Allergens</Label>
          <CollapsibleChipList
            options={allergens.map(allergen => ({
              id: allergen.id,
              name: allergen.name,
              iconKey: allergen.icon || null
            }))}
            selected={eatOutForm.watch('selectedAllergens')}
            onChange={(selected) => eatOutForm.setValue('selectedAllergens', selected)}
            maxVisible={6}
          >
            {(visibleOptions) => (
              <ChipSelector
                options={visibleOptions}
                selected={eatOutForm.watch('selectedAllergens')}
                onChange={(selected) => eatOutForm.setValue('selectedAllergens', selected)}
                mode="multiple"
                colorScheme="amber"
                type="allergen"
              />
            )}
          </CollapsibleChipList>
        </div>

        {/* Details Section */}
        <div>
          <Label className="text-foreground font-medium">Tags</Label>
          <TagInput
            tags={eatOutForm.watch('tags')}
            onChange={(tags) => eatOutForm.setValue('tags', tags)}
            placeholder="Add flavors, keywords, etc..."
            className="mt-1"
          />
        </div>

        <div>
          <Label className="text-foreground font-medium">Description</Label>
          <Textarea
            placeholder="Tell us what made this dish special..."
            {...eatOutForm.register('description')}
            className="mt-1 border-border bg-background text-foreground h-24"
          />
        </div>

      </form>
    </div>
  );
};

// EatInForm Component
interface EatInFormProps {
  id?: string;
  initialData: EatInFormData | null;
  dietaryClaims: Array<{id: string, name: string, icon?: string}>;
  allergens: Array<{id: string, name: string, icon?: string}>;
  isSubmitting: boolean;
  mode: 'create' | 'edit';
  onSubmit: (data: EatInFormData) => Promise<void>;
  onCanSubmitChange: (canSubmit: boolean) => void;
  mediaUploaderRef: React.RefObject<SubmissionMediaUploaderHandle>;
}

const EatInForm: React.FC<EatInFormProps> = ({
  id,
  initialData,
  dietaryClaims,
  allergens,
  isSubmitting,
  mode,
  onSubmit,
  onCanSubmitChange,
  mediaUploaderRef,
}) => {
  const eatInForm = useForm<EatInFormData>({
    defaultValues: initialData || {
      itemName: '',
      restaurantName: '',
      price: null,
      deliveryPlatform: '',
      itemLink: '',
      description: '',
      imageUrls: [],
      videoUrls: [],
      cuisine: [],
      tags: [],
      selectedClaims: [],
      selectedAllergens: [],
      googlePlaceId: undefined,
      restaurantAddress: undefined,
      restaurantLat: undefined,
      restaurantLng: undefined,
      countryCode: undefined,
      ratingValue: null,
      ratingPortion: null,
      ratingTaste: null,
      reviewTags: [],
      coreCategory: null,
      timeOfDay: []
    }
  });

  useEffect(() => {
    if (initialData) {
      eatInForm.reset(initialData);
    }
  }, [initialData, eatInForm]);

  const watchedItemName = eatInForm.watch('itemName');
  const watchedRestaurantNameIn = eatInForm.watch('restaurantName');
  const watchedImageUrlsIn = eatInForm.watch('imageUrls');

  useEffect(() => {
    onCanSubmitChange(
      watchedItemName.trim() !== '' &&
      watchedRestaurantNameIn.trim() !== '' &&
      watchedImageUrlsIn.length > 0
    );
  }, [watchedItemName, watchedRestaurantNameIn, watchedImageUrlsIn, onCanSubmitChange]);

  return (
    <div>
      <form id={id} onSubmit={eatInForm.handleSubmit(onSubmit)} className="space-y-6">
        {/* Hero Section - Photos & Videos */}
        <SubmissionMediaUploader
          ref={mediaUploaderRef}
          imageUrls={eatInForm.watch('imageUrls')}
          videoUrls={eatInForm.watch('videoUrls')}
          onImagesChange={(urls) => eatInForm.setValue('imageUrls', urls)}
          onVideosChange={(urls) => eatInForm.setValue('videoUrls', urls)}
        />

        {/* Identity Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-foreground font-medium">Item Name *</Label>
            <Input
              placeholder="e.g., Spicy Chicken Sandwich"
              {...eatInForm.register('itemName')}
              className="mt-1 border-border bg-background text-foreground"
            />
          </div>
          <div>
            <RestaurantSearchInput
              label="Restaurant / Cloud Kitchen *"
              placeholder="Search for a kitchen or restaurant..."
              initialValue={eatInForm.watch('restaurantName')}
              onSelect={(data) => {
                eatInForm.setValue('restaurantName', data.name);
                eatInForm.setValue('googlePlaceId', data.place_id);
                eatInForm.setValue('restaurantAddress', data.address);
                eatInForm.setValue('restaurantLat', data.lat);
                eatInForm.setValue('restaurantLng', data.lng);
                eatInForm.setValue('countryCode', data.country_code);
              }}
            />
          </div>
        </div>

        <div>
          <Label className="text-foreground font-medium">Price</Label>
          <Input
            placeholder="0.00"
            type="number"
            step="0.01"
            {...eatInForm.register('price', { valueAsNumber: true })}
            className="mt-1 border-border bg-background text-foreground"
          />
        </div>

        <div>
          <Label className="text-foreground font-medium">Platform (e.g., UberEats)</Label>
          <Input
            placeholder="e.g., Uber Eats, DoorDash, Grocery Store"
            {...eatInForm.register('deliveryPlatform')}
            className="mt-1 border-border bg-background text-foreground"
          />
        </div>

        <div>
          <Label className="text-foreground font-medium">Link to Item</Label>
          <Input
            placeholder="Direct link to order this item"
            {...eatInForm.register('itemLink')}
            className="mt-1 border-border bg-background text-foreground"
          />
        </div>

        {/* Review Section */}
        <ReviewMatrix
          value={{
            ratingValue: eatInForm.watch('ratingValue') as any,
            ratingPortion: eatInForm.watch('ratingPortion') as any,
            ratingTaste: eatInForm.watch('ratingTaste') as any
          }}
          onChange={(key, value) => eatInForm.setValue(key as any, value)}
        />

        {/* Tag Selector */}
        <TagSelector
          selected={eatInForm.watch('reviewTags')}
          onChange={(tags) => eatInForm.setValue('reviewTags', tags)}
        />

        {/* Classification Section */}
        <div>
          <Label className="text-foreground font-medium mb-2 block">Cuisine Type</Label>
          <CuisineSelector
            value={eatInForm.watch('cuisine')}
            onChange={(value) => eatInForm.setValue('cuisine', value)}
          />
        </div>

        <div>
          <Label className="text-foreground font-medium mb-2 block">Dish Type</Label>
          <SimpleChipSelector
            options={CORE_CATEGORY_OPTIONS}
            selected={eatInForm.watch('coreCategory') ? [eatInForm.watch('coreCategory')!] : []}
            onChange={(vals) => eatInForm.setValue('coreCategory', vals[0] ?? null)}
            mode="single"
            columns={3}
          />
        </div>

        <div>
          <Label className="text-foreground font-medium mb-2 block">Best For</Label>
          <SimpleChipSelector
            options={TIME_OF_DAY_OPTIONS}
            selected={eatInForm.watch('timeOfDay')}
            onChange={(vals) => eatInForm.setValue('timeOfDay', vals)}
            mode="multiple"
            columns={2}
          />
        </div>

        <div>
          <Label className="text-foreground font-medium mb-2 block">Dietary Claims</Label>
          <CollapsibleChipList
            options={dietaryClaims.map(claim => ({
              id: claim.id,
              name: claim.name,
              iconKey: claim.icon || null
            }))}
            selected={eatInForm.watch('selectedClaims')}
            onChange={(selected) => eatInForm.setValue('selectedClaims', selected)}
            maxVisible={6}
          >
            {(visibleOptions) => (
              <ChipSelector
                options={visibleOptions}
                selected={eatInForm.watch('selectedClaims')}
                onChange={(selected) => eatInForm.setValue('selectedClaims', selected)}
                mode="multiple"
                colorScheme="green"
                type="dietary"
              />
            )}
          </CollapsibleChipList>
        </div>

        <div>
          <Label className="text-foreground font-medium mb-2 block">Contains Allergens</Label>
          <CollapsibleChipList
            options={allergens.map(allergen => ({
              id: allergen.id,
              name: allergen.name,
              iconKey: allergen.icon || null
            }))}
            selected={eatInForm.watch('selectedAllergens')}
            onChange={(selected) => eatInForm.setValue('selectedAllergens', selected)}
            maxVisible={6}
          >
            {(visibleOptions) => (
              <ChipSelector
                options={visibleOptions}
                selected={eatInForm.watch('selectedAllergens')}
                onChange={(selected) => eatInForm.setValue('selectedAllergens', selected)}
                mode="multiple"
                colorScheme="amber"
                type="allergen"
              />
            )}
          </CollapsibleChipList>
        </div>

        {/* Details Section */}
        <div>
          <Label className="text-foreground font-medium">Tags</Label>
          <TagInput
            tags={eatInForm.watch('tags')}
            onChange={(tags) => eatInForm.setValue('tags', tags)}
            placeholder="Add flavors, keywords, etc..."
            className="mt-1"
          />
        </div>

        <div>
          <Label className="text-foreground font-medium">Description</Label>
          <Textarea
            placeholder="What makes this item worth trying?"
            {...eatInForm.register('description')}
            className="mt-1 border-border bg-background text-foreground h-24"
          />
        </div>

      </form>
    </div>
  );
};

export const SubmissionFlow: React.FC<SubmissionFlowProps> = ({ 
  isOpen, 
  onClose, 
  mode = 'create', 
  itemType,
  itemToEdit,
  prefillData,
  onDishCreated
}) => {
  // Draft persistence - stores form progress in localStorage
  const [draft, setDraft, clearDraft] = useLocalStorage<{
    isEatOut: boolean;
    formData: EatOutFormData | EatInFormData | null;
    timestamp: number;
  } | null>(DRAFT_KEY, null);

  const [isEatOut, setIsEatOut] = useState(true);
  const [canSubmit, setCanSubmit] = useState(false);
  const handleToggleEatOut = () => {
    setIsEatOut(prev => !prev);
    setInitialEditData(null);
    setCanSubmit(false);
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false); // Ref-based lock to prevent rage clicks
  const eatOutUploaderRef = useRef<SubmissionMediaUploaderHandle>(null);
  const eatInUploaderRef = useRef<SubmissionMediaUploaderHandle>(null);
  const [isLoadingEditData, setIsLoadingEditData] = useState(false);
  const [initialEditData, setInitialEditData] = useState<EatOutFormData | EatInFormData | null>(null);
  const queryClient = useQueryClient();

  // Dropdown options
  const [dietaryClaims, setDietaryClaims] = useState<Array<{id: string, name: string, icon?: string}>>([]);
  const [allergens, setAllergens] = useState<Array<{id: string, name: string, icon?: string}>>([]);
  const [deliveryPlatforms, setDeliveryPlatforms] = useState<Array<{id: string, name: string}>>([]);

  // Function to save current form state as draft
  const saveDraft = (formData: EatOutFormData | EatInFormData) => {
    if (mode === 'create' && !prefillData) {
      setDraft({
        isEatOut,
        formData,
        timestamp: Date.now()
      });
    }
  };

  // Load form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (prefillData) {
        // Pre-fill mode: set service type and pre-populate fields
        setIsEatOut(prefillData.serviceType === 'dine_in');
        // If mediaType is 'video', do NOT pre-fill imageUrls - user must upload a dish cover photo
        setInitialEditData({
          dishName: '',
          restaurantName: prefillData.restaurantName,
          chef: '',
          price: null,
          description: '',
          imageUrls: prefillData.mediaType === 'video' ? [] : (prefillData.imageUrl ? [prefillData.imageUrl] : []),
          videoUrls: [],
          cuisine: [],
          tags: [],
          selectedClaims: [],
          selectedAllergens: [],
          googlePlaceId: prefillData.googlePlaceId,
          restaurantAddress: prefillData.restaurantAddress,
          restaurantLat: prefillData.restaurantLat,
          restaurantLng: prefillData.restaurantLng,
          ratingValue: null,
          ratingPortion: null,
          ratingTaste: null,
          reviewTags: []
        } as EatOutFormData);
      } else if (mode === 'create') {
        // Check for existing draft (only if less than 24 hours old)
        const DRAFT_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
        if (draft && (Date.now() - draft.timestamp) < DRAFT_EXPIRY) {
          setIsEatOut(draft.isEatOut);
          if (draft.formData) {
            setInitialEditData(draft.formData);
          }
          toast.info('Restored your previous draft');
        } else {
          setInitialEditData(null);
          // Clear expired draft
          if (draft) clearDraft();
        }
      }
    }
  }, [isOpen, mode, prefillData]);

  // Fetch dietary claims and allergens for editing
  const fetchRelations = async (item: any) => {
    try {
      const [claimsResult, allergensResult] = await Promise.all([
        supabase.from('dish_claims').select('claim_id').eq('dish_id', item.id),
        supabase.from('dish_allergens').select('allergen_id').eq('dish_id', item.id)
      ]);
      
      return {
        claims: claimsResult.data?.map(c => c.claim_id) || [],
        allergens: allergensResult.data?.map(a => a.allergen_id) || []
      };
    } catch (error) {
      console.error('Error fetching relations:', error);
      return { claims: [], allergens: [] };
    }
  };

  // Fetch the original submission post with ratings and tags
  const fetchOriginalPost = async (dishId: string) => {
    if (!dishId) return null;
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('rating_value, rating_portion, rating_taste, tags, image_url, image_urls, user_id')
        .eq('dish_id', dishId)
        .order('created_at', { ascending: true }) // Get the very first post
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching original post for edit:', error);
        return null;
      }
      return data;
    } catch (error) {
      console.error('Error in fetchOriginalPost:', error);
      return null;
    }
  };

  // Load edit data and store it in state
  useEffect(() => {
    if (mode === 'edit' && itemToEdit && itemType) {
      const loadEditData = async () => {
        try {
          setIsLoadingEditData(true);
          setIsEatOut(itemType === 'dish');

          // Fetch relations and original post data in parallel
          const [relations, originalPost] = await Promise.all([
            fetchRelations(itemToEdit),
            fetchOriginalPost(itemToEdit.id)
          ]);

          if (itemType === 'dish') {
            const eatOutFormData: EatOutFormData = {
              dishName: itemToEdit.name || '',
              restaurantName: itemToEdit.restaurants?.name || itemToEdit.restaurant || itemToEdit.brand || '',
              googlePlaceId: itemToEdit.restaurants?.google_place_id || itemToEdit.google_place_id || undefined,
              chef: itemToEdit.chef || '',
              price: itemToEdit.price ? Number(itemToEdit.price) : null,
              description: itemToEdit.description || '',
              imageUrls: originalPost?.image_urls?.length ? originalPost.image_urls : originalPost?.image_url ? [originalPost.image_url] : itemToEdit.image_url ? [itemToEdit.image_url] : (itemToEdit.image ? [itemToEdit.image] : []),
              videoUrls: [],
              cuisine: Array.isArray(itemToEdit.cuisine) ? itemToEdit.cuisine : (itemToEdit.cuisine ? [itemToEdit.cuisine] : []),
              tags: itemToEdit.tags ? [...new Set(itemToEdit.tags as string[])] : [],
              selectedClaims: relations.claims,
              selectedAllergens: relations.allergens,
              // Pre-populate ratings from original post
              ratingValue: originalPost?.rating_value || null,
              ratingPortion: originalPost?.rating_portion || null,
              ratingTaste: originalPost?.rating_taste || null,
              reviewTags: originalPost?.tags || [],
              coreCategory: itemToEdit.core_category || null,
              timeOfDay: itemToEdit.time_of_day || []
            };
            setInitialEditData(eatOutFormData);
          } else {
            const eatInFormData: EatInFormData = {
              itemName: itemToEdit.name || '',
              restaurantName: itemToEdit.brand || itemToEdit.restaurant || '',
              price: itemToEdit.price ? Number(itemToEdit.price) : null,
              deliveryPlatform: itemToEdit.where_to_buy || '',
              itemLink: itemToEdit.platform_link || '',
              description: itemToEdit.description || '',
              imageUrls: originalPost?.image_urls?.length ? originalPost.image_urls : originalPost?.image_url ? [originalPost.image_url] : itemToEdit.image_url ? [itemToEdit.image_url] : (itemToEdit.image ? [itemToEdit.image] : []),
              videoUrls: [],
              cuisine: Array.isArray(itemToEdit.cuisine) ? itemToEdit.cuisine : (itemToEdit.cuisine ? [itemToEdit.cuisine] : []),
              tags: itemToEdit.tags ? [...new Set(itemToEdit.tags as string[])] : [],
              selectedClaims: relations.claims,
              selectedAllergens: relations.allergens,
              // Pre-populate ratings from original post
              ratingValue: originalPost?.rating_value || null,
              ratingPortion: originalPost?.rating_portion || null,
              ratingTaste: originalPost?.rating_taste || null,
              reviewTags: originalPost?.tags || [],
              coreCategory: itemToEdit.core_category || null,
              timeOfDay: itemToEdit.time_of_day || []
            };
            setInitialEditData(eatInFormData);
          }
        } catch (error) {
          console.error('Error loading edit data:', error);
          toast.error('Failed to load item data');
        } finally {
          setIsLoadingEditData(false);
        }
      };

      loadEditData();
    }
  }, [mode, itemToEdit, itemType]);

  // Fetch reference data when component mounts
  React.useEffect(() => {
    if (isOpen) {
      fetchReferenceData();
    }
  }, [isOpen]);

  const fetchReferenceData = async () => {
    try {
      const [claimsResult, allergensResult, platformsResult] = await Promise.all([
        supabase.from('dietary_claims').select('id, name, icon'),
        supabase.from('allergens').select('id, name, icon'),
        supabase.from('delivery_platforms').select('id, name')
      ]);

      if (claimsResult.data) setDietaryClaims(claimsResult.data);
      if (allergensResult.data) setAllergens(allergensResult.data);
      if (platformsResult.data) setDeliveryPlatforms(platformsResult.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const resetAndClose = () => {
    setIsEatOut(true);
    setInitialEditData(null);
    // Clear the draft on close/cancel
    clearDraft();
    onClose();
  };

  const handleEatOutSubmit = async (data: EatOutFormData) => {
    // Validate before locking so taps on the button always give feedback
    if (!data.dishName?.trim() || !data.restaurantName?.trim()) {
      const missingFields: string[] = [];
      if (!data.dishName?.trim()) missingFields.push('Dish Name');
      if (!data.restaurantName?.trim()) missingFields.push('Restaurant Name');
      toast.error(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }
    if (data.imageUrls.length === 0) {
      toast.error('Please add at least one photo');
      return;
    }

    // Rage click guard - ref-based lock
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    // Upload all selected media files now (deferred from selection time)
    let uploadedImageUrls = data.imageUrls;
    let uploadedVideoUrls = data.videoUrls;
    try {
      const result = await eatOutUploaderRef.current!.uploadAll();
      uploadedImageUrls = result.imageUrls;
      uploadedVideoUrls = result.videoUrls;
      if (uploadedImageUrls.length === 0) {
        toast.error('Please add at least one photo');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
    } catch {
      toast.error('Failed to upload media. Please try again.');
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    console.log('[SUBMIT_EATOUT] 🚀 Starting submission', {
      dishName: data.dishName,
      restaurantName: data.restaurantName,
      imageCount: uploadedImageUrls.length,
      videoCount: uploadedVideoUrls.length,
      hasCuisine: data.cuisine.length > 0,
      claimsCount: data.selectedClaims.length,
      allergensCount: data.selectedAllergens.length,
      hasGooglePlaceId: !!data.googlePlaceId
    });
    
    try {
      console.log('[SUBMIT_EATOUT] 🔐 Checking authentication...');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log('[SUBMIT_EATOUT] ❌ Not authenticated');
        toast.error('Please sign in to submit');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
      
      console.log('[SUBMIT_EATOUT] ✅ Authenticated as', user.id);

      // Check if it's a valid number (and not NaN)
      const finalPrice = (data.price !== null && !isNaN(data.price)) ? data.price : null;

      if (mode === 'edit' && itemToEdit) {
        // Update existing dish
        let dishUpdateData: any = {
          name: data.dishName,
          chef: data.chef || null,
          price: finalPrice,
          description: data.description || null,
          image_url: uploadedImageUrls[0] || null,
          cuisine: data.cuisine.length > 0 ? data.cuisine : null,
          core_category: (data.coreCategory || null) as any,
          time_of_day: data.timeOfDay.length > 0 ? data.timeOfDay : null,
          updated_at: new Date().toISOString()
        };

        // Handle restaurant change if Google Place ID changed
        if (data.googlePlaceId && data.googlePlaceId !== itemToEdit.restaurants?.google_place_id) {
          // Check if new restaurant exists or create it
          const { data: existingRestaurants } = await supabase
            .from('restaurants')
            .select('id')
            .eq('google_place_id', data.googlePlaceId)
            .limit(1);

          let newRestaurantId: string;
          if (existingRestaurants && existingRestaurants.length > 0) {
            newRestaurantId = existingRestaurants[0].id;
          } else {
            // Create new restaurant
            const { data: newRestaurant, error: restaurantError } = await supabase
              .from('restaurants')
              .insert({
                name: data.restaurantName,
                google_place_id: data.googlePlaceId,
                address: data.restaurantAddress || null,
                latitude: data.restaurantLat || null,
                longitude: data.restaurantLng || null,
                country_code: data.countryCode || null
              })
              .select('id')
              .single();

            if (restaurantError) throw restaurantError;
            newRestaurantId = newRestaurant.id;
          }
          dishUpdateData.restaurant_id = newRestaurantId;
        }

        const { error: dishError } = await supabase
          .from('dishes')
          .update(dishUpdateData)
          .eq('id', itemToEdit.id);

        if (dishError) throw dishError;

        // Update tags
        await supabase.from('submission_tags').delete().eq('dish_id', itemToEdit.id);
        if (data.tags.length > 0) {
          const uniqueTags = [...new Set(data.tags)];
          const tagInserts = uniqueTags.map(tag => ({
            dish_id: itemToEdit.id,
            tag
          }));
          await supabase.from('submission_tags').insert(tagInserts);
        }

        // Update dietary claims
        await supabase.from('dish_claims').delete().eq('dish_id', itemToEdit.id);
        if (data.selectedClaims.length > 0) {
          const claimInserts = data.selectedClaims.map(claimId => ({
            dish_id: itemToEdit.id,
            claim_id: claimId
          }));
          await supabase.from('dish_claims').insert(claimInserts);
        }

        // Update allergens
        await supabase.from('dish_allergens').delete().eq('dish_id', itemToEdit.id);
        if (data.selectedAllergens.length > 0) {
          const allergenInserts = data.selectedAllergens.map(allergenId => ({
            dish_id: itemToEdit.id,
            allergen_id: allergenId
          }));
          await supabase.from('dish_allergens').insert(allergenInserts);
        }

        // Update the original post with new ratings
        if (itemToEdit.id) {
          const { data: originalPost } = await supabase
            .from('posts')
            .select('id')
            .eq('dish_id', itemToEdit.id)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

          if (originalPost) {
            const postUpdatePayload: any = {
              image_url: uploadedImageUrls[0] || null,
              image_urls: uploadedImageUrls,
              rating_value: data.ratingValue || null,
              rating_portion: data.ratingPortion || null,
              rating_taste: data.ratingTaste || null,
              tags: data.reviewTags.length > 0 ? data.reviewTags : null,
              updated_at: new Date().toISOString()
            };

            const { error: postUpdateError } = await supabase
              .from('posts')
              .update(postUpdatePayload)
              .eq('id', originalPost.id);

            if (postUpdateError) {
              console.error('[EDIT] Failed to update post ratings:', postUpdateError);
            } else {
              console.log('[EDIT] ✅ Updated original post ratings');
            }
          }
        }

        toast.success('Dish updated successfully!');
      } else {
        // ========== ATOMIC SUBMISSION VIA RPC ==========
        // This replaces the previous multi-step client-side logic with a single atomic database call
        // that handles restaurant creation/lookup, dish upsert, and post creation
        
        // Valid cuisine values that match the database ENUM
        const VALID_CUISINES = [
          'italian', 'chinese', 'japanese', 'mexican', 'indian', 'french', 'thai', 'greek',
          'american', 'mediterranean', 'korean', 'vietnamese', 'spanish', 'middle_eastern', 'turkish', 'other'
        ];
        const validatedCuisines = data.cuisine.filter(c => VALID_CUISINES.includes(c));
        const validatedCuisine = validatedCuisines.length > 0 ? validatedCuisines : null;

        console.log('[SUBMIT_EATOUT] 🚀 Calling atomic upsert_dish_submission RPC...');
        
        const coverImageUrl = uploadedImageUrls[0] ?? '';

        const { data: rpcResult, error: rpcError } = await supabase.rpc('upsert_dish_submission', {
          // Required parameters
          p_restaurant_name: data.restaurantName,
          p_dish_name: data.dishName.trim(),
          p_image_url: coverImageUrl,
          p_image_urls: uploadedImageUrls.length > 0 ? uploadedImageUrls : null,
          p_video_urls: null, // videos create a separate Drool post below
          // Optional restaurant data
          p_google_place_id: data.googlePlaceId || null,
          p_restaurant_address: data.restaurantAddress || null,
          p_restaurant_lat: data.restaurantLat || null,
          p_restaurant_lng: data.restaurantLng || null,
          p_country_code: data.countryCode || null,
          // Optional dish data
          p_chef: data.chef || null,
          p_price: finalPrice,
          p_description: data.description || null,
          p_cuisine: validatedCuisine,
          p_service_type: prefillData?.serviceType || 'dine_in',
          // Post/Review data
          p_rating_value: data.ratingValue || null,
          p_rating_portion: data.ratingPortion || null,
          p_rating_taste: data.ratingTaste || null,
          p_review_tags: data.reviewTags.length > 0 ? data.reviewTags : null,
          p_price_reported: data.price || null,
          p_content: data.description || null,
          // Related data arrays
          p_submission_tags: data.tags.length > 0 ? data.tags : null,
          p_dietary_claim_ids: data.selectedClaims.length > 0 ? data.selectedClaims : null,
          p_allergen_ids: data.selectedAllergens.length > 0 ? data.selectedAllergens : null,
          // Skip post creation when called from Drool flow (video context)
          p_skip_post_creation: prefillData?.skipPostCreation ?? false,
          // Structured categories
          p_core_category: data.coreCategory || null,
          p_time_of_day: data.timeOfDay.length > 0 ? data.timeOfDay : null
        });

        if (rpcError) {
          console.error('[SUBMIT_EATOUT] ❌ RPC error:', rpcError);
          throw new Error(rpcError.message || 'Failed to submit dish');
        }

        const result = rpcResult as { success: boolean; dish_id?: string; is_new_dish?: boolean; error?: string };
        
        if (!result.success) {
          console.error('[SUBMIT_EATOUT] ❌ RPC returned failure:', result.error);
          throw new Error(result.error || 'Failed to submit dish');
        }

        const dishId = result.dish_id;
        const isNewDish = result.is_new_dish;

        // Store dish ID so the feed can scroll to and highlight it after modal closes
        if (dishId) {
          sessionStorage.setItem('new_dish_id', dishId);
        }

        console.log('[SUBMIT_EATOUT] ✅ RPC success:', { dishId, isNewDish });

        // If videos were uploaded, create a separate Drool post linked to the same dish
        if (uploadedVideoUrls.length > 0 && dishId) {
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          if (currentUser) {
            await supabase.from('posts').insert({
              user_id: currentUser.id,
              dish_id: dishId,
              image_url: null,
              image_urls: [],
              video_url: uploadedVideoUrls[0],
              video_urls: uploadedVideoUrls,
              media_type: 'video' as const,
              content: data.description || null,
              rating_value: (data.ratingValue || null) as any,
              rating_portion: (data.ratingPortion || null) as any,
              rating_taste: (data.ratingTaste || null) as any,
              tags: data.reviewTags.length > 0 ? data.reviewTags : null,
            });
          }
        }

        // Track the appropriate event
        if (isNewDish) {
          trackEvent('Submission', 'Create Dish', prefillData?.serviceType || 'dine_in');
          toast.success('Dish submitted successfully!');
        } else {
          trackEvent('Submission', 'Add Post to Existing Dish', prefillData?.serviceType || 'dine_in');
          toast.success('Your snap was added to this dish! 📸');
        }
        
        // If onDishCreated callback exists, call it and return early
        if (onDishCreated && dishId) {
          console.log('[SUBMIT_EATOUT] 📞 Calling onDishCreated callback');
          onDishCreated(dishId, data.dishName);
          setIsSubmitting(false);
          return;
        }
      }

      // Invalidate relevant queries
      console.log('[SUBMIT_EATOUT] 🔄 Invalidating queries...');
      queryClient.invalidateQueries({ queryKey: ['dishes'] });
      queryClient.invalidateQueries({ queryKey: ['whatsNew'] });
      queryClient.invalidateQueries({ queryKey: ['userDishes'] });
      queryClient.invalidateQueries({ queryKey: ['chartFeed'] });
      queryClient.invalidateQueries({ queryKey: ['snaps'] });
      // Invalidate specific dish metadata and gallery for immediate gallery refresh
      if (itemToEdit?.id) {
        queryClient.invalidateQueries({ queryKey: ['dish-metadata', itemToEdit.id] });
        queryClient.invalidateQueries({ queryKey: ['dish-gallery', itemToEdit.id] });
        queryClient.invalidateQueries({ queryKey: ['dish-story-posts', itemToEdit.id] });
        if (itemToEdit.slug) {
          queryClient.invalidateQueries({ queryKey: ['dish-metadata', itemToEdit.slug] });
        }
      }
      
      resetAndClose();
    } catch (error) {
      console.error('[SUBMIT_EATOUT] ❌ Error:', error);
      toast.error(`Failed to ${mode === 'edit' ? 'update' : 'submit'} dish. Please try again.`);
    } finally {
      console.log('[SUBMIT_EATOUT] 🏁 Finally block - resetting state');
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleEatInSubmit = async (data: EatInFormData) => {
    // Validate before locking so taps on the button always give feedback
    if (!data.itemName?.trim() || !data.restaurantName?.trim()) {
      toast.error('Please fill in required fields');
      return;
    }
    if (data.imageUrls.length === 0) {
      toast.error('Please add at least one photo');
      return;
    }

    // Rage click guard - ref-based lock
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    // Upload all selected media files now (deferred from selection time)
    let uploadedImageUrls = data.imageUrls;
    let uploadedVideoUrls = data.videoUrls;
    try {
      const result = await eatInUploaderRef.current!.uploadAll();
      uploadedImageUrls = result.imageUrls;
      uploadedVideoUrls = result.videoUrls;
      if (uploadedImageUrls.length === 0) {
        toast.error('Please add at least one photo');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
    } catch {
      toast.error('Failed to upload media. Please try again.');
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    console.log('[SUBMIT_EATIN] 🚀 Starting submission', {
      itemName: data.itemName,
      restaurantName: data.restaurantName,
      imageCount: uploadedImageUrls.length,
      videoCount: uploadedVideoUrls.length,
      hasCuisine: data.cuisine.length > 0,
      claimsCount: data.selectedClaims.length,
      allergensCount: data.selectedAllergens.length,
      hasGooglePlaceId: !!data.googlePlaceId,
      deliveryPlatform: data.deliveryPlatform
    });
    
    try {
      console.log('[SUBMIT_EATIN] 🔐 Checking authentication...');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log('[SUBMIT_EATIN] ❌ Not authenticated');
        toast.error('Please sign in to submit');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
      
      console.log('[SUBMIT_EATIN] ✅ Authenticated as', user.id);

      // Check if it's a valid number (and not NaN)
      const finalPrice = (data.price !== null && !isNaN(data.price)) ? data.price : null;

      if (mode === 'edit' && itemToEdit) {
        // Update existing dish (takeaway)
        const dishUpdateData: any = {
          name: data.itemName,
          brand: data.restaurantName,
          service_type: 'takeaway',
          core_category: (data.coreCategory || null) as any,
          time_of_day: data.timeOfDay.length > 0 ? data.timeOfDay : null,
          price: finalPrice,
          description: data.description || null,
          image_url: uploadedImageUrls[0] || null,
          cuisine: data.cuisine.length > 0 ? data.cuisine : null,
          updated_at: new Date().toISOString()
        };

        const { error: dishError } = await supabase
          .from('dishes')
          .update(dishUpdateData)
          .eq('id', itemToEdit.id);

        if (dishError) throw dishError;

        // Update tags
        await supabase.from('submission_tags').delete().eq('dish_id', itemToEdit.id);
        if (data.tags.length > 0) {
          const uniqueTags = [...new Set(data.tags)];
          const tagInserts = uniqueTags.map(tag => ({
            dish_id: itemToEdit.id,
            tag
          }));
          await supabase.from('submission_tags').insert(tagInserts);
        }

        // Update dietary claims
        await supabase.from('dish_claims').delete().eq('dish_id', itemToEdit.id);
        if (data.selectedClaims.length > 0) {
          const claimInserts = data.selectedClaims.map(claimId => ({
            dish_id: itemToEdit.id,
            claim_id: claimId
          }));
          await supabase.from('dish_claims').insert(claimInserts);
        }

        // Update allergens
        await supabase.from('dish_allergens').delete().eq('dish_id', itemToEdit.id);
        if (data.selectedAllergens.length > 0) {
          const allergenInserts = data.selectedAllergens.map(allergenId => ({
            dish_id: itemToEdit.id,
            allergen_id: allergenId
          }));
          await supabase.from('dish_allergens').insert(allergenInserts);
        }

        // Update platform links
        await supabase.from('dish_platforms').delete().eq('dish_id', itemToEdit.id);
        if (data.deliveryPlatform && data.itemLink) {
          await supabase.from('dish_platforms').insert({
            dish_id: itemToEdit.id,
            platform_id: data.deliveryPlatform,
            order_url: data.itemLink
          });
        }

        // Update the original post with new ratings
        if (itemToEdit.id) {
          const { data: originalPost } = await supabase
            .from('posts')
            .select('id')
            .eq('dish_id', itemToEdit.id)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

          if (originalPost) {
            const postUpdatePayload: any = {
              image_url: uploadedImageUrls[0] || null,
              image_urls: uploadedImageUrls,
              rating_value: data.ratingValue || null,
              rating_portion: data.ratingPortion || null,
              rating_taste: data.ratingTaste || null,
              tags: data.reviewTags.length > 0 ? data.reviewTags : null,
              updated_at: new Date().toISOString()
            };

            const { error: postUpdateError } = await supabase
              .from('posts')
              .update(postUpdatePayload)
              .eq('id', originalPost.id);

            if (postUpdateError) {
              console.error('[EDIT_EATIN] Failed to update post ratings:', postUpdateError);
            } else {
              console.log('[EDIT_EATIN] ✅ Updated original post ratings');
            }
          }
        }

        toast.success('Dish updated successfully!');
      } else {
        // Create new dish (takeaway)
        // Handle restaurant lookup/creation (same as Eat Out)
        let restaurantId = '00000000-0000-0000-0000-000000000001'; // Fallback

        if (data.googlePlaceId) {
          // Check if restaurant exists
          const { data: existingRestaurant } = await supabase
            .from('restaurants')
            .select('id')
            .eq('google_place_id', data.googlePlaceId)
            .maybeSingle();

          if (existingRestaurant) {
            restaurantId = existingRestaurant.id;
          } else {
            // Create new restaurant with location data
            const { data: newRestaurant, error: restaurantError } = await supabase
              .from('restaurants')
              .insert({
                name: data.restaurantName,
                google_place_id: data.googlePlaceId,
                address: data.restaurantAddress,
                latitude: data.restaurantLat,
                longitude: data.restaurantLng,
                country_code: data.countryCode || null
              })
              .select('id')
              .single();

            if (restaurantError) throw restaurantError;
            restaurantId = newRestaurant.id;
          }
        }

        const eatInCoverUrl = uploadedImageUrls[0] ?? '';

        const dishInsertData = {
          name: data.itemName,
          brand: data.restaurantName,
          service_type: 'takeaway' as const,
          core_category: (data.coreCategory || null) as any,
          time_of_day: data.timeOfDay.length > 0 ? data.timeOfDay : null,
          restaurant_id: restaurantId,
          price: finalPrice,
          description: data.description || null,
          image_url: eatInCoverUrl,
          cuisine: data.cuisine.length > 0 ? data.cuisine : null,
          submitted_by: user.id
        };

        const { data: dish, error: dishError } = await supabase
          .from('dishes')
          .insert(dishInsertData)
          .select('id')
          .single();

        if (dishError) throw dishError;

        if (data.tags.length > 0) {
          const tagInserts = data.tags.map(tag => ({
            dish_id: dish.id,
            tag
          }));
          await supabase.from('submission_tags').insert(tagInserts);
        }

        if (data.selectedClaims.length > 0) {
          const claimInserts = data.selectedClaims.map(claimId => ({
            dish_id: dish.id,
            claim_id: claimId
          }));
          await supabase.from('dish_claims').insert(claimInserts);
        }

        if (data.selectedAllergens.length > 0) {
          const allergenInserts = data.selectedAllergens.map(allergenId => ({
            dish_id: dish.id,
            allergen_id: allergenId
          }));
          await supabase.from('dish_allergens').insert(allergenInserts);
        }

        // Insert platform link
        if (data.deliveryPlatform && data.itemLink) {
          await supabase.from('dish_platforms').insert({
            dish_id: dish.id,
            platform_id: data.deliveryPlatform,
            order_url: data.itemLink
          });
        }

        // MANDATORY: Create Genesis Post for King of the Hill eligibility
        // A dish CANNOT exist without its post - this is transaction-like behavior
        // Genesis Snap post (images only — always required)
        console.log('[SUBMIT_EATIN] 📸 Creating Genesis Snap Post (mandatory)...');
        const { error: postError } = await supabase.from('posts').insert({
          user_id: user.id,
          dish_id: dish.id,
          image_url: eatInCoverUrl,
          image_urls: uploadedImageUrls,
          video_url: null,
          video_urls: [],
          media_type: 'image' as const,
          content: data.description || 'Original submission',
          rating_value: (data.ratingValue || null) as any,
          rating_portion: (data.ratingPortion || null) as any,
          rating_taste: (data.ratingTaste || null) as any,
          tags: data.reviewTags.length > 0 ? data.reviewTags : null
        });

        if (postError) {
          console.error('[SUBMIT_EATIN] ❌ Genesis Post creation FAILED - rolling back dish:', postError);
          const { error: rollbackError } = await supabase.from('dishes').delete().eq('id', dish.id);
          if (rollbackError) {
            console.error('[SUBMIT_EATIN] ❌ Rollback also failed:', rollbackError);
          }
          throw new Error('Failed to create submission post. Please try again.');
        }
        console.log('[SUBMIT_EATIN] ✅ Genesis Snap Post created successfully');

        // If videos were uploaded, create a separate Drool post
        if (uploadedVideoUrls.length > 0) {
          const { error: droolError } = await supabase.from('posts').insert({
            user_id: user.id,
            dish_id: dish.id,
            image_url: null,
            image_urls: [],
            video_url: uploadedVideoUrls[0],
            video_urls: uploadedVideoUrls,
            media_type: 'video' as const,
            content: data.description || null,
            rating_value: (data.ratingValue || null) as any,
            rating_portion: (data.ratingPortion || null) as any,
            rating_taste: (data.ratingTaste || null) as any,
            tags: data.reviewTags.length > 0 ? data.reviewTags : null,
          });
          if (droolError) {
            console.error('[SUBMIT_EATIN] ⚠️ Drool post creation failed (non-fatal):', droolError);
          } else {
            console.log('[SUBMIT_EATIN] ✅ Drool post created successfully');
          }
        }

        trackEvent('Submission', 'Create Dish', 'takeaway');
        toast.success('Dish submitted successfully!');
      }

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['dishes'] });
      queryClient.invalidateQueries({ queryKey: ['whatsNew'] });
      queryClient.invalidateQueries({ queryKey: ['userDishes'] });
      queryClient.invalidateQueries({ queryKey: ['snaps'] });
      if (itemToEdit?.id) {
        queryClient.invalidateQueries({ queryKey: ['dish-gallery', itemToEdit.id] });
        queryClient.invalidateQueries({ queryKey: ['dish-story-posts', itemToEdit.id] });
      }

      resetAndClose();
    } catch (error) {
      console.error('Error submitting dish:', error);
      toast.error(`Failed to ${mode === 'edit' ? 'update' : 'submit'} dish. Please try again.`);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={resetAndClose}>
      <SheetContent
        side="bottom"
        className="h-[96vh] flex flex-col p-0 rounded-t-2xl border-t border-border [&>button]:hidden"
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 px-4 pt-4 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="w-16" />
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-1.5">
              <UtensilsCrossed className="w-4 h-4" />
              {mode === 'edit' ? 'Edit Dish' : 'New Dish'}
            </h2>
            <div className="w-16 flex justify-end">
              <Button variant="outline" size="sm" onClick={resetAndClose}>
                Cancel
              </Button>
            </div>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-4 py-5">
          {/* Eat Out / Eat In selector — create mode only */}
          {mode === 'create' && !prefillData && (
            <div className="flex gap-2 mb-5">
              <button
                type="button"
                onClick={() => { if (!isEatOut) handleToggleEatOut(); }}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                  isEatOut
                    ? 'bg-[#cfef00] text-black border-[#cfef00]'
                    : 'bg-transparent text-muted-foreground border-border'
                }`}
              >
                <span className="flex flex-col items-center gap-0.5">
                  <span>🍽️ Dine in</span>
                  <span className="text-xs font-normal opacity-70">Ate at the restaurant</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => { if (isEatOut) handleToggleEatOut(); }}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                  !isEatOut
                    ? 'bg-[#cfef00] text-black border-[#cfef00]'
                    : 'bg-transparent text-muted-foreground border-border'
                }`}
              >
                <span className="flex flex-col items-center gap-0.5">
                  <span>🥡 Takeaway</span>
                  <span className="text-xs font-normal opacity-70">Ordered to go</span>
                </span>
              </button>
            </div>
          )}

          {(isLoadingEditData || (mode === 'edit' && !initialEditData)) ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : isEatOut ? (
            <EatOutForm
              key={itemToEdit ? itemToEdit.id : 'create-dish'}
              id="submission-form"
              initialData={initialEditData as EatOutFormData | null}
              dietaryClaims={dietaryClaims}
              allergens={allergens}
              isSubmitting={isSubmitting}
              mode={mode}
              onSubmit={handleEatOutSubmit}
              isVideoContext={prefillData?.mediaType === 'video'}
              restaurantPrefilled={!!prefillData?.restaurantName}
              onCanSubmitChange={setCanSubmit}
              mediaUploaderRef={eatOutUploaderRef}
            />
          ) : (
            <EatInForm
              key={itemToEdit ? itemToEdit.id : 'create-product'}
              id="submission-form"
              initialData={mode === 'edit' ? initialEditData as EatInFormData : null}
              dietaryClaims={dietaryClaims}
              allergens={allergens}
              isSubmitting={isSubmitting}
              mode={mode}
              onSubmit={handleEatInSubmit}
              onCanSubmitChange={setCanSubmit}
              mediaUploaderRef={eatInUploaderRef}
            />
          )}
        </div>

        {/* Sticky Footer */}
        <div className="flex-shrink-0 px-4 pb-6 pt-3 border-t border-border bg-background">
          <Button
            form="submission-form"
            type="submit"
            disabled={isSubmitting}
            className="w-full h-12 text-base font-semibold bg-[#cfef00] hover:bg-[#cfef00]/90 text-black disabled:opacity-40"
          >
            {isSubmitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
            ) : (
              mode === 'edit'
                ? (isEatOut ? 'Update Dish' : 'Update Product')
                : 'Post Dish'
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
